import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import TableComponent from "../../../atoms/TableComponent/DataTable";
import axiosInstance from "../../../lib/axios";
import { usePermissions } from "../../../GlobalFunction/userPermissions";
import { Box, Button, Tooltip } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';

const columns = [
  { id: "RowNum", label: "S.No.", minWidth: 50 },
  { id: "AccGrpName", label: "Group Name", minWidth: 120 },
  { id: "AccGrpabvr", label: "Abbreviation", minWidth: 100 },
  { id: "UnderGroupName", label: "Under Group", minWidth: 120 },
  { id: "Status", label: "Status", minWidth: 80 },
  { id: "Remark", label: "Remark", minWidth: 150 },
];

export default function GroupMstTable() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerms, setSearchTerms] = useState({});
  const [rows, setRows] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const navigate = useNavigate();

  const permissions = usePermissions("Groups");
  const { AddRec = "0" } = permissions || {};

  useEffect(() => {
    fetchGroupDropdown();
  }, []);

  useEffect(() => {
    if (groupOptions.length > 0) {
      fetchAccountGroupData();
    }
  }, [groupOptions, searchTerms, page, rowsPerPage]);

  const fetchGroupDropdown = async () => {
    try {
      const response = await axiosInstance.post("AccmGrp/GetAccmGrpDrp", {});
      if (response.data.Status === 0 && Array.isArray(response.data.Data)) {
        const formattedOptions = response.data.Data.map((item) => ({
          Id: item.AccmgrpId,
          Name: item.AccmgrpName,
        }));
        setGroupOptions(formattedOptions);
      } else {
        console.error("No group dropdown data available:", response.data.Message);
      }
    } catch (error) {
      console.error("Error fetching group dropdown:", error);
    }
  };

  // Fetching the group dashboard
  const fetchAccountGroupData = async () => {
    try {
      const response = await axiosInstance.post("AccGrp/GetAccGrpDashBoard", {
        start: page * rowsPerPage + 1,
        PageSize: rowsPerPage,
        SearchText: Object.values(searchTerms).filter(Boolean).join(" ") || "",
      });

      if (response.data.Status === 0 && Array.isArray(response.data.Data)) {
        const formattedData = response.data.Data.map((groupData, index) => ({
          ...groupData,
          RowNum: page * rowsPerPage + index + 1,
          AccGrpName: groupData.AccGrpName || "",
          AccGrpabvr: groupData.AccGrpabvr || "",
          UnderGroupName: groupOptions.find((g) => g.Id === groupData.AccmGrpId)?.Name || "N/A",
          Status: groupData.Status === null ? "" : groupData.Status == "1" ? "Active" : "Inactive",
          Remark: groupData.Remark || "",
        }));
        setRows(formattedData);
      } else {
        console.error("Error fetching account group data:", response.data.Message);
        setRows([]);
      }
    } catch (error) {
      console.error("Error fetching account group data:", error);
      setRows([]);
    }
  };

  const handlePermittedClick = () => {
    if (AddRec === "1") {
      handleClick();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleClick = () => {
    navigate("/groupmaster");
  };

  const handleHomeClick = () => {
    navigate("/dashboard");
  };

  const handleSearchChange = (columnId, value) => {
    setSearchTerms((prev) => ({ ...prev, [columnId]: value }));
    setPage(0);
  };

  const filteredRows = React.useMemo(() => {
    return rows.filter((row) =>
      Object.entries(searchTerms).every(([columnId, term]) => {
        if (!term) return true;
        const value = row[columnId];
        if (typeof value === "string") {
          return value.toLowerCase().includes(term.toLowerCase());
        } else if (typeof value === "number") {
          return value.toString().includes(term);
        }
        return true;
      })
    );
  }, [rows, searchTerms]);

  const handleRowDoubleClick = (row) => {
    navigate("/groupmaster", { state: { selectedRow: row, mode: "view" } });
  };

  const handleLocationclick = () => {
    navigate("/masters/accounts");
  };

  const addButtonStyles = {
    background: AddRec === "1" ? "#635bff" : "rgba(0, 0, 0, 0.12)",
    color: AddRec === "1" ? "white" : "rgba(0, 0, 0, 0.26)",
    borderRadius: "50px",
    padding: "8px 20px",
    boxShadow: AddRec === "1" ? "0px 6px 15px rgba(0, 0, 0, 0.3)" : "none",
    transition: "background 0.3s ease",
    "&:hover": {
      background: AddRec === "1" ? "#524ccc" : "rgba(0, 0, 0, 0.12)",
    },
    "&:disabled": {
      backgroundColor: "rgba(0, 0, 0, 0.12)",
      color: "rgba(0, 0, 0, 0.26)",
    },
  };

  const Filters = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Tooltip title="You have no permission to add record" placement="top">
        <span>
          <Button
            variant="contained"
            onClick={handlePermittedClick}
            size="small"
            disabled={true}
            sx={addButtonStyles}
          >
            <AddIcon />New
          </Button>
        </span>
      </Tooltip>

    </Box>
  );

  return (
    <TableComponent
      columns={columns}
      rows={rows}
      filteredRows={filteredRows}
      page={page}
      title="Accounts"
      titleMaster="Groups"
      rowsPerPage={rowsPerPage}
      FiltersJsx={Filters}
      handleRowDoubleClick={handleRowDoubleClick}
      handleSearchChange={handleSearchChange}
      handleChangePage={handleChangePage}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      handleClick={handleClick}
      handleHomeClick={handleHomeClick}
      handleLocationclick={handleLocationclick}
    />
  );
}